import {
  Pricebook,
  PricebookEntry,
  LineItemOverride,
  QuoteLineItem,
  LineItemConfig,
  LineItemDisplay,
  LineItemDisplayTotals,
  QuoteTotals,
  OrderLocation,
  LocationSummary,
  ProductCategory,
  CategorySummary,
  LineItemConfigInstructions,
  BaseLineItemConfig,
  FeatureTotals,
  FeatureCounts,
  TieredPrice
} from '@nuso/quoting/types';

export const DEFAULT_TERM_MONTHS = 36;

import { addDollars, calcSalesPriceForMargin, multiplyDollars, subtractDollars } from '@nuso/common/util-money';

import * as _ from 'lodash';



// We pass the canEdit in, as once it is submitted, things are locked
// and we may be viewing a quote that has some line items that are related to inactive
// pricebook entries. If can edit, zero out and hide those.


// Intended to be used across the entire quote so can calc all of the totals
export function calcQuoteLineItemConfigs(
    originalPricebookEntries: PricebookEntry[],
    lineItems: QuoteLineItem[],
    overrides: LineItemOverride[],
    termMonths: number | null,
    defaultMarginPercentage: number,
    canEdit: boolean,
    tollFreeUsaegRate: string
): LineItemConfig[] {
  if (!termMonths) {
    termMonths = DEFAULT_TERM_MONTHS;
  }

  const pricebookEntries = originalPricebookEntries.map(entry => applyQuoteSpecificSettingsToEntry(entry, tollFreeUsaegRate))

  const allConfigs = calcBaseLineItemConfigs(pricebookEntries, lineItems, overrides, termMonths, defaultMarginPercentage);
  const baseConfigs = canEdit ? allConfigs.filter(c => c.pricebookEntry.active) : allConfigs;
  const existingSetupFeeConfigs = baseConfigs.filter(c => c.pricebookEntry.categoryKey === 'one-time-charges' && c.pricebookEntry.disableQuantityEdit)
  const baseWithoutSetupFeeConfigs = baseConfigs.filter(c => !(c.pricebookEntry.categoryKey === 'one-time-charges' && c.pricebookEntry.disableQuantityEdit))
  const setupFeeConfigs = calcSetupFeeConfigs(pricebookEntries, existingSetupFeeConfigs, baseConfigs, termMonths, defaultMarginPercentage);
  const realConfigs = [...baseWithoutSetupFeeConfigs, ...setupFeeConfigs];


  const virtualConfigs = calcVirtualIncludedLineItemConfigs(pricebookEntries, realConfigs)

  // now apply the virtual configs. For display, they may need to be grouped (may do that in display stuff)
  // at a minimum here, we need to calc the "includedQuantities" on the real configs
  const configsWithIncludedQuantities = realConfigs.map(config => {
    const { hasIncludedKey } = config.pricebookEntry;
    if (!hasIncludedKey) {
      return {
        ...config,
        includedQuantity: 0,
        totalQuantity: config.quantity
      };
    }

    const virtualConfig = virtualConfigs.find((vc) =>
      vc.locationId === config.locationId &&
      vc.pricebookEntry.hasIncludedKey === hasIncludedKey);

    if (!virtualConfig) {
      return {
        ...config,
        includedQuantity: 0,
        totalQuantity: config.quantity
      }
    }

    return {
      ...config,
      includedQuantity: virtualConfig.quantity,
      totalQuantity: config.quantity + virtualConfig.quantity
    }
  })

  return [
    ...configsWithIncludedQuantities,
    ...virtualConfigs
  ]
}


/*
  per location, we get the line item displays, and the blown out line item displays by category

  we need to add to this (or somewhere) the calculation of includedQuantities
  I think we pass all of the PKG- products for this location into a function that gets back the calculated totals
  of all of those items (should be able to use totalsForLineSubtotals(packageLineItems))
  Then, we get all of the PRD-s that have some element attached... then use the element that is
  attached to lookup the includedQuantity from those package totals.
  We want to calculate this info here, as we can make the decision to
  1. For the quote display, have virtual $0 line items to show included things
  2. For the quote edit (catSummaries) have the includedQuantity set so we can display
  const packageLineItems = lineItems.filter(li => li.isPackage);
  const includedCounts = totalsForLineSubtotals(packageLineItems);
  // 1. For the quote display, have virtual $0 line items to show included things
  // --- calculate virtual line items for the given
  const virtualIncludedLineItems = calcVirtualIncludedLineItems(includedCounts, packageLineItems)
*/
function calcFeatureTotalsByLocation(
  baseConfigs: LineItemConfig[]
): { locationId: string, featureTotals: FeatureTotals }[] {
  const packageConfigs = baseConfigs.filter(li => li.pricebookEntry.isPackage);
  const packagesConfigsByLocation = _.groupBy(packageConfigs, 'locationId') // => { '9021abax': [{c}...], 'lmno123': [{c...}]} where c is config

  return _.map(packagesConfigsByLocation, (configsForLocation, locationId) => ({
    locationId,
    featureTotals: featureTotalsForLineItemConfigs(configsForLocation),
  }));
}

function calcVirtualIncludedLineItemConfigs(
  pricebookEntries: PricebookEntry[],
  baseConfigs: LineItemConfig[],
): LineItemConfig[] {

  const featuresByLocation = calcFeatureTotalsByLocation(baseConfigs)

  return _.chain(featuresByLocation)
    .map(({featureTotals, locationId}) => {
      const featureKeys = _.chain(featureTotals)
          .pickBy((quantity) => quantity > 0)
          .map((quantity, key) => key.replace('Total', ''))
          .value();

      return _.map(featureKeys, (key) => {
        const pricebookEntry = pricebookEntries.find(e => e.hasIncludedKey === key);
        if (!pricebookEntry) {
          // legit may not be one... minutes for example
          return undefined;
        }

        const quantity = (featureTotals as unknown as Record<string, number>)[`${key}Total`];
        const baseConfig = {
          locationId,
          pricebookEntry
        };

        return {
          ...baseConfig,
          quantity,
          unitPrice: 0, // These are included items, so no  pricing
          retailPrice: 0,
          termRetailPrice: 0,
          partnerPrice: 0,
          basePartnerPrice: 0,
          ...emptyFeatureTotals,
          virtual: true
        }
      })
    })
    .flatten()
    .compact()
    .value();
}


interface LocationIdEntryIdTuple {
  locationId: string;
  pricebookEntryId: string;
}


// This is the first pass,
function calcBaseLineItemConfigs(
  pricebookEntries: PricebookEntry[],
  lineItems: QuoteLineItem[],
  overrides: LineItemOverride[],
  termMonths: number,
  defaultMarginPercentage: number,
): LineItemConfig[] {
  // There may be a better strategy to combine these. For now, going to
  // 1. From lineItems and overrides each, grab { locationId, pricebookEntryId }
  // 2. Get the uniq ones from combining those two arrays
  // 3. map the resulting array and lookup in each...
  const fromLineItems = lineItems.map(({ locationId, pricebookEntryId }) => ({ locationId, pricebookEntryId }));
  const fromOverrides = overrides.map(({ locationId, pricebookEntryId }) => ({ locationId, pricebookEntryId }));

  const combined: LocationIdEntryIdTuple[] = _.uniqWith([...fromLineItems, ...fromOverrides], _.isEqual);

  return combined.map(({ locationId, pricebookEntryId }) => {
    const baseConfig: BaseLineItemConfig = {
      locationId,
      // Not provable that pricebookEntries always has a match for pricebookEntryId;
      // filtering/throwing here would change output for a case this batch can't confirm never happens.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      pricebookEntry: pricebookEntries.find((pbe) => pbe.id === pricebookEntryId)!,
      lineItem: lineItems.find((li) => li.locationId === locationId && li.pricebookEntryId === pricebookEntryId ),
      override: overrides.find((o) => o.locationId === locationId && o.pricebookEntryId === pricebookEntryId),
    };

    return lineItemConfigWithCalculatedValues(baseConfig, termMonths, defaultMarginPercentage);
  })
}

// Items that require a setup config have a setupFeeProductId attached.
// We go look up the pricebook entry associated with that pricebook id
// and then add a new line item in with the configs.
function calcSetupFeeConfigs(
  pricebookEntries: PricebookEntry[],
  existingSetupFeeConfigs: LineItemConfig[],
  baseConfigs: LineItemConfig[],
  termMonths: number | null,
  defaultMarginPercentage: number
): LineItemConfig[] {

return _.chain(baseConfigs)
  .filter(config => !!config.pricebookEntry.setupFeeProductId) // just get the ones that have setup fees attached
  .map((sourceConfig: LineItemConfig) => {
    const { name, productId, setupFeeProductId } = sourceConfig.pricebookEntry;
    const setupFeeEntry = pricebookEntries.find(e => e.productId === setupFeeProductId);
    if (!setupFeeEntry) {
      console.log(`PRICEBOOK ENTRY MISSING for setup fee(${setupFeeProductId}). Referenced from ${name}(${productId})`)
      return undefined;
    }

    // if there is an existing config, we will use the price set on that
    // otherwise will use the price calculated from the setupFeeEntry
    const quantity = sourceConfig.quantity;

    const termPrice = calcTermRetailPrice(setupFeeEntry, termMonths, quantity, defaultMarginPercentage);
    const termPartnerPrice = calcTermPartnerPrice(setupFeeEntry, termMonths, quantity);

    const existingConfigForSetupFee = existingSetupFeeConfigs.find(existing =>
        existing.locationId === sourceConfig.locationId &&
        existing.pricebookEntry.id === setupFeeEntry.id)

    const { locationId } = sourceConfig;
    const { id: pricebookEntryId } = setupFeeEntry;

    // we used to default to 0, but see long note below where we calcTermPrice
    const unitPrice = firstNonEmptyValue([existingConfigForSetupFee?.unitPrice, termPrice]);

    const baseSetupConfig = {
      locationId: sourceConfig.locationId,
      pricebookEntry: setupFeeEntry,
      override: {
        id: `${locationId}-${pricebookEntryId}`,
        locationId,
        pricebookEntryId,
        quantity,
        unitPrice,
        partnerPrice: firstNonEmptyValue([existingConfigForSetupFee?.partnerPrice, termPartnerPrice])
      }
    }

    return lineItemConfigWithCalculatedValues(baseSetupConfig, termMonths, defaultMarginPercentage);
  })
  .compact()
  .value()
}


// based on the line item configs, figure out which items to create,update,delete QuoteLineItems for in salesforce
// We need to delete any line items that now should be quantity zero
// we need to update ane line items where the quantity or price has changed (and has quantity)
// we need to create any new line items that has an postive quantity override and no pre-existing line item
export function lineItemConfigInstructions(lineItemConfigs: LineItemConfig[]): LineItemConfigInstructions {
  const realConfigs = lineItemConfigs.filter(c => !c.virtual);
  const deletes = realConfigs.filter(c => c.lineItem && c.override && c.override.quantity === 0);
  const updates = realConfigs.filter(c =>
    c.lineItem &&
    c.override &&
    c.override.quantity !== 0 &&
      ( c.lineItem.quantity !== c.override.quantity ||
        c.lineItem.unitPrice !== c.override.unitPrice ||
        c.lineItem.partnerUnitPrice !== c.override.partnerPrice
      )
    );
  const creates = realConfigs.filter(c => !c.lineItem && c.override && c.override.quantity !== 0)

  return {
    deletes,
    updates,
    creates
  }
}

export function mergeLineItemOverrides(lineItemOverrides: LineItemOverride[], newOverrides: LineItemOverride[]) {
  if (newOverrides.length === 0) {
    return lineItemOverrides;
  }

  // Right now we aren't worried about order of the overrides...
  // So we are going to remove the item if exists and add
  // the new one to the front.
  // Mainly doing this because it is easier to code up while tired
  // has the side effect of next time we find, it will be at the front
  // since typical use case will be emitting events as up arrow hit
  // on a quantity, kind of a win

  const untouched = lineItemOverrides.filter(o =>
    !newOverrides.find(n => o.locationId === n.locationId && o.pricebookEntryId === n.pricebookEntryId)
  )

  return [...newOverrides, ...untouched];
}


function lineItemConfigWithCalculatedValues(
  baseConfig: BaseLineItemConfig,
  termMonths: number | null,
  defaultMarginPercentage: number
): LineItemConfig {
  const { pricebookEntry, lineItem, override } = baseConfig;

  const retailPrice = pricebookEntry.retailPrice;


  const quantity = firstNonEmptyValue([
    override?.quantity,
    lineItem?.quantity,
    0
  ])

  const termRetailPrice = calcTermRetailPrice(pricebookEntry, termMonths, quantity, defaultMarginPercentage);

  // if termRetailPrice is null, then this product not available for the current term
  // we need to set the price to null, and the quantity to 0
  const unitPrice = termRetailPrice === null ? null : firstNonEmptyValue([
    override?.unitPrice,
    lineItem?.unitPrice,
    termRetailPrice
  ]);

  const basePartnerPrice = pricebookEntry.partnerPrice;
  const partnerTermPrice = calcTermPartnerPrice(pricebookEntry, termMonths, quantity);
  const partnerPrice = firstNonEmptyValue([
    override?.partnerPrice,
    lineItem?.partnerUnitPrice,
    partnerTermPrice
  ])

  return {
    ...baseConfig,
    unitPrice,
    quantity: termRetailPrice === null ? 0 : quantity,
    retailPrice,
    termRetailPrice,
    partnerPrice,
    basePartnerPrice,
    ...calcSingleItemFeatureTotals(pricebookEntry, quantity)
  }
}


export function calcOverridesFromTermChange(
  lineItemConfigs: LineItemConfig[],
  termMonths: number,
  defaultMarginPercentage: number
): LineItemOverride[] {
  const realConfigs = lineItemConfigs.filter(c => !c.virtual);

  return realConfigs.map(c => ({
    id: `${c.locationId}-${c.pricebookEntry.id}`,
    locationId: c.locationId,
    pricebookEntryId: c.pricebookEntry.id,
    quantity: c.quantity,
    unitPrice: calcTermRetailPrice(c.pricebookEntry, termMonths, c.quantity, defaultMarginPercentage),
    partnerPrice: calcTermPartnerPrice(c.pricebookEntry, termMonths, c.quantity),
  }))

}

export function calcLineItemDisplays(lineItemConfigs: LineItemConfig[]): LineItemDisplay[] {

  return lineItemConfigs.map((c) => {
    const {
      locationId,
      pricebookEntry,
      lineItem,
      unitPrice,
      quantity,
      retailPrice,
      termRetailPrice,
      partnerPrice,
      basePartnerPrice,
      includedQuantity,
      virtual,
    } = c;
    const pricebookEntryId = pricebookEntry.id;

    let description = lineItem?.description || pricebookEntry.description;
    if (virtual) {
      description = `${description} (Included)`;
    }

    return {
      id: `${locationId}-${pricebookEntryId}`,
      locationId,
      pricebookEntryId,
      categoryId: pricebookEntry.categoryId,
      description,
      subDescription: pricebookEntry.subDescription,
      active: pricebookEntry.active,
      isPackage: pricebookEntry.isPackage,
      usageType: pricebookEntry.usageType,
      hasIncludedKey: pricebookEntry.hasIncludedKey,
      disableQuantityEdit: pricebookEntry.disableQuantityEdit,
      pricebookEntry,
      includedQuantity,
      virtual,
      quantity,
      retailPrice,
      termRetailPrice,
      partnerPrice,
      basePartnerPrice,
      disableForEdit: false, // may override in the gui somewhere
      // unitPrice comes from the spread below (calcSingleItemTotals), which is the source of truth
      // includedQuantity! preserves existing behavior when undefined: quantity + undefined = NaN
      // propagates into totalQuantity. That's a real latent bug tracked as #3229, not something
      // to paper over here with a fallback -- this file was never type-checked before this batch,
      // so no behavior should change as a side effect of adding types.
      // Dropping the assertion (e.g. via `?? `) would fix bug #3229 as a side effect, out of scope here.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...calcSingleItemTotals(quantity, unitPrice, partnerPrice, pricebookEntry, includedQuantity!)
    }
  })
}

const emptyFeatureTotals = {
  didsTotal: 0,
  e911Total: 0,
  directoryListingTotal: 0,
  tollFreeTotal: 0,
  cnamTotal: 0,
  callPathsTotal: 0,
  minutesTotal: 0
};

function addFeatureTotals(totals: FeatureTotals, li: FeatureTotals): FeatureTotals {
  return {
    didsTotal: totals.didsTotal + (li.didsTotal || 0),
    e911Total: totals.e911Total + (li.e911Total || 0),
    directoryListingTotal: totals.directoryListingTotal + (li.directoryListingTotal || 0),
    tollFreeTotal: totals.tollFreeTotal + (li.tollFreeTotal || 0),
    cnamTotal: totals.cnamTotal + (li.cnamTotal || 0),
    callPathsTotal: totals.callPathsTotal + (li.callPathsTotal || 0),
    minutesTotal: totals.minutesTotal + (li.minutesTotal || 0)
  }
}

function featureTotalsForLineItemConfigs(configs: FeatureTotals[]): FeatureTotals {
  return configs.reduce((totals, li) => addFeatureTotals(totals, li), emptyFeatureTotals)
}

export function totalsForLineSubtotals(lineItems: QuoteTotals[]) {
  return lineItems.reduce((totals, li) => ({
    unitPrice: addDollars(totals.unitPrice, li.unitPrice),
    annualTotal: addDollars(totals.annualTotal, li.annualTotal),
    recurringTotal: addDollars(totals.recurringTotal, li.recurringTotal),
    oneTimeTotal: addDollars(totals.oneTimeTotal, li.oneTimeTotal),
    partnerTotalPrice: addDollars(totals.partnerTotalPrice, li.partnerTotalPrice),
    partnerRevenue: addDollars(totals.partnerRevenue, li.partnerRevenue),
    ...addFeatureTotals(totals, li)
  }), {
    unitPrice: 0,
    annualTotal: 0,
    recurringTotal: 0,
    oneTimeTotal: 0,
    partnerTotalPrice: 0,
    partnerRevenue: 0,
    ...emptyFeatureTotals
  })
}

function firstNonEmptyValue(values: any[]) {
  return values.find(v => v !== undefined && v !== null);
}

export function calcTermRetailPrice(
  pricebookEntry: PricebookEntry, termMonths: number | null, quantity: number, defaultMarginPercentage: number,
) {
  const termPrice = calcTermPrice(pricebookEntry, termMonths, quantity, 'rp');
  if (termPrice === undefined || termPrice === null) {
    return null;
  }
  return calcSalesPriceForMargin(termPrice, defaultMarginPercentage);
}

export function calcTermPartnerPrice(pricebookEntry: PricebookEntry, termMonths: number | null, quantity: number) {
  return calcTermPrice(pricebookEntry, termMonths, quantity, 'pp');
}

// Raw catalog list price for a given term — no margin markup applied.
// Returns null if the entry has no price for this term.
export function calcTermListPrice(pricebookEntry: PricebookEntry, termMonths: number | null, quantity: number) {
  return calcTermPrice(pricebookEntry, termMonths, quantity, 'rp');
}

// "'Managed Router NV 3140 EFP, Rackmount, 8x5xNBD Subscription' cannot be purchased if the term is less than 3 years.
// I left the pricing on the price book entry empty to indicate that, but it's still populating a price.
// I assume you have it defaulting to something? Will leaving the price book entry empty work
// to indicate that or do I need to add an additional field or something indicating the minimum term for a product?"
//
// Originally we were defaulting to the retail/partner price if the termPrice is null
// * completely ignore retail price and partner price
// * ONLY look at respective term price
// * IF term price is NULL, then product is not available. the product would be visible
//     but you won’t be able to add quantity (it will be 0). Ideally a hover or something show
//     (“Product not available for selected term length”.)
// If there were ANY line items associated with those products, they should be zeroed out
function calcTermPrice(pricebookEntry: PricebookEntry, termMonths: number | null, quantity: number, termPrefix: string) {
  const tieredPrice = calcTieredPricing(pricebookEntry, termMonths, quantity, termPrefix);
  if (tieredPrice) {
    return tieredPrice;
  }

  const termPrice = (pricebookEntry as unknown as Record<string, number | null>)[`${termPrefix}${termMonths}`];
  return termPrice; // see notes above. leaving below to help understand what was

  // Originally we assume a term price of 0 would be honored and did this check
  // (termPrice !== null && termPrice !== undefined) ? termPrice : the retail
  // But appears we should just assume if 0 to use the retail as well
  // return (termPrice !== null && termPrice !== undefined) ? termPrice : pricebookEntry[priceField];
}

function calcTieredPricing(pricebookEntry: PricebookEntry, termMonths: number | null, quantity: number, termPrefix: string) {
  if (!pricebookEntry.tieredPricing) {
    return null;
  }

  const priceEntry = findTieredPricing(pricebookEntry.tieredPricing, quantity);
  if (!priceEntry) {
    return null;
  }

  return (priceEntry as unknown as Record<string, number | null>)[`${termPrefix}${termMonths}`];
}

export function findTieredPricing(tieredPricing: TieredPrice[], quantity: number) {
  return tieredPricing.find(p => p.start <= quantity && (p.stop === null || p.stop >= quantity))
}


// In order to give a free E911 with each location. We added a
// Max_Quantity__c in the meta.

// Null or 0 means no max. Anything else means when we calculate the
// included, we limit to the max.
function includedTotal(includedCount: number, quantity: number, maxIncludedCount: number | null = null): number {
  const total = quantity * includedCount;
  return maxIncludedCount ? Math.min(maxIncludedCount, total) : total;
}

function calcSingleItemFeatureTotals(counts: FeatureCounts, quantity: number): FeatureTotals {
  const {
    didCount, e911Count, directoryListingCount, tollFreeCount, callPathCount, minutesCount,
    maxIncludedDidCount, maxIncludedE911Count, maxIncludedDirectoryListingCount, maxIncludedTollFreeCount,
    maxIncludedCallPathCount, maxIncludedMinutesCount,
  } = counts;
  return {
    didsTotal: includedTotal(didCount, quantity, maxIncludedDidCount),
    e911Total: includedTotal(e911Count, quantity, maxIncludedE911Count),
    directoryListingTotal: includedTotal(directoryListingCount, quantity, maxIncludedDirectoryListingCount),
    tollFreeTotal: includedTotal(tollFreeCount, quantity, maxIncludedTollFreeCount),
    // CNAM doesn't flow through the per-line included-count math today
    // (no cnamCount in FeatureCounts). Zero here keeps the quote totals
    // consistent with the new required field on FeatureTotals; per-order
    // CNAM count is sourced separately from productMeta in the provisioning
    // context resolver.
    cnamTotal: 0,
    callPathsTotal: includedTotal(callPathCount, quantity, maxIncludedCallPathCount),
    minutesTotal: includedTotal(minutesCount, quantity, maxIncludedMinutesCount),
  }
}

function calcSingleItemTotals(
    quantity: number,
    unitPrice: number | null,
    partnerPrice: number | null,
    pricebookEntry: PricebookEntry,
    includedQuantity: number
): LineItemDisplayTotals {

  const { mrrType } = pricebookEntry;

  const totalPrice = multiplyDollars(unitPrice, quantity);
  const annualTotal = mrrType === 'Annual' ? totalPrice : 0;
  const recurringTotal = mrrType === 'Recurring' ? totalPrice : 0;
  const oneTimeTotal = mrrType === 'One Time' ? totalPrice : 0;

  const partnerTotalPrice = multiplyDollars(partnerPrice, quantity);

  // TODO: I think we only give revenue on recurring
  const partnerRevenue = mrrType === 'Recurring' ? subtractDollars(totalPrice, partnerTotalPrice) : 0;

  return {
    unitPrice,
    totalPrice,
    annualTotal,
    recurringTotal,
    oneTimeTotal,
    partnerTotalPrice,
    partnerRevenue,
    totalQuantity: quantity + includedQuantity,
    ...calcSingleItemFeatureTotals(pricebookEntry, quantity)
  }
}

export function calcLocationSummaries(
    lineItems: LineItemDisplay[],
    locations: OrderLocation[],
    productCategories?: ProductCategory[]
): LocationSummary[] {

 // const nonZeroLineItems = lineItems.filter(li => li.quantity);

  const byLocationId = _.groupBy(lineItems, 'locationId');

  // sort these by main first (returns boolean, lower comes first... so ! it)
  const locationSummaries =
    _.chain(locations)
    .map(({ id, name, description, isMain }) => ({
      id,
      name,
      description,
      isMain
    }))
    .uniqBy('id')
    .sortBy([(loc) => !loc.isMain, (loc) => loc.description.toLowerCase()])
    .value();

  const result = locationSummaries.map(loc => {
    // want the zero'd ones going into the cat summary, in case there are overrides
    const lineItemsForLocation = byLocationId[loc.id] || [];
    const nonZeroLineItems = lineItemsForLocation.filter(li => li.quantity);
    return {
      ...loc,
      ...totalsForLineSubtotals(nonZeroLineItems),
      lineItems: nonZeroLineItems,
      categorySummaries: productCategories && calcCategorySummaries(lineItemsForLocation , productCategories)
    }
  })

  return result;
}


export function calcCategorySummaries(lineItemDisplays: LineItemDisplay[], productCategories: ProductCategory[]): CategorySummary[] {
  const byCategory = _.groupBy(lineItemDisplays, 'categoryId');

  return productCategories.map((category) => {
    const lineItems = byCategory[category.id] || [];

    const quoteTotals = totalsForLineSubtotals(lineItems);

    return {
      ...category,
      ...quoteTotals,
      hasTollFree: quoteTotals.tollFreeTotal > 0,
      lineItems
    }
  });
}

// locationId: string,
// pricebookEntries: PricebookEntry[],
// lineItems: QuoteLineItem[],
// overrides: LineItemOverride[]

// Difference here is that
// 1 we want to expand for every location
// and then every product category
export function calcLocationSummariesForEdit(
  lineItemDisplays: LineItemDisplay[],
  locations: OrderLocation[],
  productCategories?: ProductCategory[]
): LocationSummary[] {

// sort these by main first (returns boolean, lower comes first... so ! it)
return _.chain(locations)
  .map((location: OrderLocation) => {
    const lineItems = lineItemDisplays.filter(item => item.locationId === location.id);
    return {
      ...location,
      ...totalsForLineSubtotals(lineItems),
      lineItems,
      categorySummaries: productCategories && calcCategorySummaries(lineItems, productCategories)
    };
  })
  .sortBy([(loc) => !loc.isMain, (loc) => loc.description.toLowerCase()])
  .value();
}

// Want to make sure virtual line Items for all products in category
// for the current location. The location summaries should already
// be calculated for existing/merged line items. what
// we need to do is add zeroed virtual line items for each
// product if not already an entry
export function calcEditDisplaySummariesForLocationByCategory(
  location: OrderLocation,
  pricebook: Pricebook,
  termMonths: number,
  defaultMarginPercentage: number
): CategorySummary[] {
  const { summary: existingSummary } = location;
  if (!existingSummary) {
    throw new Error('calcEditDisplaySummariesForLocationByCategory requires location.summary to already be calculated');
  }

  const { categorySummaries: existingCategorySummaries } = existingSummary;
  if (!existingCategorySummaries) {
    throw new Error('calcEditDisplaySummariesForLocationByCategory requires location.summary.categorySummaries to already be calculated');
  }

  const blownOutSummaries = existingCategorySummaries.map((summary: CategorySummary) => ({
    ...summary,
    lineItems: calcDisplaySummariesForCategorySummaryForEdit(location.id, summary, pricebook, termMonths, defaultMarginPercentage)
  }))

  return blownOutSummaries;
}


function emptyLineItemDisplay(
    locationId: string,
    pricebookEntry: PricebookEntry,
    termMonths: number,
    defaultMarginPercentage: number
): LineItemDisplay {
  const pricebookEntryId = pricebookEntry.id;
  const quantity = 0;


  const retailPrice = pricebookEntry.retailPrice;
  const termRetailPrice = calcTermRetailPrice(pricebookEntry, termMonths, quantity, defaultMarginPercentage);
  const unitPrice = termRetailPrice;
  const basePartnerPrice = pricebookEntry.partnerPrice;
  const partnerPrice = calcTermPartnerPrice(pricebookEntry, termMonths, quantity);

  const includedQuantity = 0;

  return {
    id: `${locationId}-${pricebookEntryId}`,
    locationId,
    pricebookEntryId,
    categoryId: pricebookEntry.categoryId,
    description: pricebookEntry.description,
    subDescription: pricebookEntry.subDescription,
    active: pricebookEntry.active,
    isPackage: pricebookEntry.isPackage,
    usageType: pricebookEntry.usageType,
    hasIncludedKey: pricebookEntry.hasIncludedKey,
    disableQuantityEdit: pricebookEntry.disableQuantityEdit,
    pricebookEntry,
    quantity,
    partnerPrice,
    basePartnerPrice,
    retailPrice,
    termRetailPrice,
    includedQuantity,
    disableForEdit: false, // may override in the gui somewhere
    // unitPrice comes from the spread below (calcSingleItemTotals), which is the source of truth
    ...calcSingleItemTotals(quantity, unitPrice, partnerPrice, pricebookEntry, includedQuantity)
  }
}

// Generates the blown out line items
function calcDisplaySummariesForCategorySummaryForEdit(
  locationId: string,
  catSummary: CategorySummary,
  pricebook: Pricebook,
  termMonths: number,
  defaultMarginPercentage: number
): LineItemDisplay[] {
  // For the given category, get all of the products in that category, and then use the existing line item if there,
  // otherwise create empty line items

  const realItems = catSummary.lineItems.filter(li => !li.virtual);
  const virtualItems = catSummary.lineItems.filter(li => li.virtual);

  const pricebookEntries = pricebook.pricebookEntries.filter(entry => entry.categoryId === catSummary.id && entry.active);
  // const pricebookEntries = pricebook.pricebookEntries.filter(entry => entry.categoryId === catSummary.id); // uncomment for testing

  return pricebookEntries.map(entry => {
    const existingReal = realItems.find(li => li.pricebookEntryId === entry.id);
    const existingVirtual = virtualItems.find(li => li.pricebookEntryId === entry.id);
    if (existingReal) {
        return existingReal;
    }

    const emptyItem = emptyLineItemDisplay(locationId, entry, termMonths, defaultMarginPercentage);
    if (!existingVirtual) {
      return emptyItem;
    }

    // if there is a virtual, we need to update the empty with calculated values...
    return {
      ...emptyItem,
      includedQuantity: existingVirtual.quantity || 0,
      totalQuantity: existingVirtual.quantity || 0
    }
  });
}

// filter out the blown out line item displays for a given category key
export function summaryFor(currentLocation: OrderLocation, catKey: string): CategorySummary {
  // Callers pass a catKey expected to exist in categorySummaries; not locally provable, and
  // returning undefined/throwing here would change behavior for a case this batch can't confirm.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return (currentLocation.summary?.categorySummaries || [])
    .find(summary => summary.key === catKey)!;
}


function applyQuoteSpecificSettingsToEntry(entry: PricebookEntry, tollFreeUsageRate: string) {
  if (entry.name === 'Toll Free Number' && tollFreeUsageRate) {
    return {
      ...entry,
      description: `Toll Free Number - $${tollFreeUsageRate} per minute`,
      subDescription: null // may want to put the usage per minute here, but then need to update display items...
    }
  }

  return entry;
}

export function nonEmptyLocationSummaries(
  lineItems: QuoteLineItem[],
  locations: OrderLocation[],
): any[] {
  const byLocationId = _.groupBy(lineItems, 'locationId');
  const locationSummaries =
    _.chain(locations)
    .map(({ id, name, description, isMain }) => ({
      id,
      name,
      description,
      isMain
    }))
    .uniqBy('id')
    .sortBy([(loc) => !loc.isMain, (loc) => loc.description.toLowerCase()])
    .value();

  const result = locationSummaries.map(loc => {
    const lineItemsForLocation = byLocationId[loc.id] || [];
    const nonZeroLineItems = lineItemsForLocation.filter(li => li.quantity);
    return {
      ...loc,
      ...totalsForLineSubtotals(nonZeroLineItems),
      lineItems: nonZeroLineItems,
    }
  })

  return result.filter(t => t.lineItems && t.lineItems.length);
}

export function nonEmptyQuoteLocationSummaries(locationSummaries: LocationSummary[]) {
  return locationSummaries.filter(t => t.lineItems && t.lineItems.length);
}
